import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { fetchChains, fetchAssets, fetchSettings, type Chain, type Asset } from "../lib/api";
import type { Settings } from "../shared/types";
import { getMe, isStandaloneJwt, getIdentityId } from "../lib/auth";
import { getUserOverrides, setUserOverrides, clearUserOverrides, type UserOverrides } from "../lib/userOverrides";
import { authHeaders } from "../lib/auth";
import { apiUrl } from "../lib/apiBase";
import { useExpertMode } from "../context/ExpertModeContext";
import Editor from "react-simple-code-editor";
import Prism from "prismjs";
import "prismjs/components/prism-json";

function highlightCode(code: string): string {
  return Prism.highlight(code, Prism.languages.json, "json");
}

import { checkBtcHealth, checkBchHealth, checkXlmHealth } from "../lib/providerDetect";
import { DOGE_BLOCKCHAIR } from "../lib/chains/dogeTx";
import { AddressBookPanel } from "./AddressBookPanel";
import { useSetExpertMode } from "../context/ExpertModeContext";
import { useTranslation } from "react-i18next";
import { setLanguage, getStoredLanguage } from "../i18n/i18n";
import { LOCALES } from "../i18n/locales";
import { isNotifyEnabled, setNotifyEnabled } from "../lib/notify";
import { friendlyError } from "../lib/error-message";
import { isRecoveryMode } from "../lib/recovery";
import { isRecoveryActive } from "../lib/recovery-flag";
import { ThemeSelector } from "./theme-selector";
import { ConfirmDialog } from "./ui/confirm-dialog";
import { trackExpertTool, trackExpertToolOnce } from "../lib/expert-usage";
import { SignOutConfirmDialog } from "./sign-out-confirm-dialog";
import { TestnetBadge } from "./testnet-badge";
import { isTestnetChain } from "../lib/testnet";
import { fetchRpcChainId } from "../lib/custom-networks";
import { CustomNetworksSection } from "./config/CustomNetworksSection";
import { PlanCard } from "./premium/PlanCard";

type RpcStatus = "checking" | "ok" | "error" | "mismatch";

/** EVM: the RPC must answer eth_chainId with the network's own id ("mismatch" = sends there are refused). */
async function rpcStatusOf(url: string, chain: { type: string; evmChainId: number | null }): Promise<RpcStatus> {
  if (chain.type === "evm" && chain.evmChainId) {
    return fetchRpcChainId(url).then((id) => (id === chain.evmChainId ? "ok" : "mismatch"), () => "error");
  }
  return (await checkRpcHealth(url, chain.type)) ? "ok" : "error";
}

async function checkRpcHealth(rpcUrl: string, chainType: string): Promise<boolean> {
  if (!rpcUrl) return false;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 5000);
  try {
    switch (chainType) {
      case "evm": {
        const res = await fetch(rpcUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ jsonrpc: "2.0", id: 1, method: "eth_blockNumber", params: [] }),
          signal: controller.signal,
        });
        if (!res.ok) return false;
        const data = await res.json();
        return !!data.result;
      }
      case "solana": {
        const res = await fetch(rpcUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ jsonrpc: "2.0", id: 1, method: "getHealth", params: [] }),
          signal: controller.signal,
        });
        if (!res.ok) return false;
        const data = await res.json();
        return data.result === "ok";
      }
      case "xrp": {
        const res = await fetch(rpcUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ method: "server_info", params: [{}] }),
          signal: controller.signal,
        });
        return res.ok;
      }
      case "btc":
      case "ltc":
        return checkBtcHealth(rpcUrl, controller.signal);
      case "bch":
        return checkBchHealth(rpcUrl, controller.signal);
      case "doge": {
        // DOGE reads go through Blockchair, not the configured rpcUrl.
        const res = await fetch(`${DOGE_BLOCKCHAIR}/stats`, { signal: controller.signal });
        return res.ok;
      }
      case "xlm":
        return checkXlmHealth(rpcUrl, controller.signal);
      default: {
        const res = await fetch(rpcUrl, { signal: controller.signal });
        return res.ok;
      }
    }
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

/** Notification toggle — on/off via localStorage, requests browser permission when enabling */
function NotificationToggle() {
  const { t } = useTranslation();
  const supported = "Notification" in window;
  const [enabled, setEnabled] = useState(() => isNotifyEnabled());
  const denied = supported && Notification.permission === "denied";

  const handleToggle = useCallback(async () => {
    if (!supported) return;
    if (enabled) {
      setNotifyEnabled(false);
      setEnabled(false);
      return;
    }
    // Request permission if not yet granted
    if (Notification.permission !== "granted") {
      const result = await Notification.requestPermission();
      if (result !== "granted") return;
    }
    setNotifyEnabled(true);
    setEnabled(true);
  }, [supported, enabled]);

  if (!supported) return null;

  return (
    <div className="px-3 md:px-5 py-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-text-primary">{t("config.notifications")}</p>
          <p className="text-xs text-text-muted mt-0.5">
            {denied ? t("config.notificationsDenied") : t("config.notificationsDesc")}
          </p>
        </div>
        <button
          onClick={handleToggle}
          disabled={denied}
          className={`relative w-8 h-[18px] rounded-full transition-colors shrink-0 ${
            enabled ? "bg-blue-500" : "bg-surface-tertiary"
          } ${denied ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <span className={`absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white transition-transform ${
            enabled ? "left-[16px]" : "left-[2px]"
          }`} />
        </button>
      </div>
    </div>
  );
}

const REFRESH_OPTIONS = [
  { label: "30s", value: 30 },
  { label: "1m", value: 60 },
  { label: "5m", value: 300 },
];

export function ConfigPage() {
  const { t } = useTranslation();
  const expert = useExpertMode();
  const setExpertContext = useSetExpertMode();
  const [chains, setChains] = useState<Chain[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [serverSettings, setServerSettings] = useState<Settings>({});
  const [serverDefaults, setServerDefaults] = useState<string[]>([]);
  const [serverRefresh, setServerRefresh] = useState(60);
  const [overrides, setOverrides] = useState<UserOverrides>({});
  const [userId, setUserId] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [expandedChain, setExpandedChain] = useState<string | null>(null);
  const [rpcStatus, setRpcStatus] = useState<Record<string, RpcStatus>>({});
  const [saved, setSaved] = useState(false);
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonTab, setJsonTab] = useState<"edit" | "preview">("edit");
  const [jsonText, setJsonText] = useState("");
  const [jsonError, setJsonError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const healthCheckTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  useEffect(() => {
    // Recovery mode: no /me call, the server may hang instead of refusing
    Promise.all([fetchChains(), fetchAssets(), fetchSettings(), isRecoveryActive() ? null : getMe()]).then(([c, a, s, me]) => {
      setChains(c);
      setAssets(a);
      setServerSettings(s);
      setServerDefaults((s.default_chains as string[]) ?? []);
      if (s.refresh_interval && typeof s.refresh_interval === "number") {
        setServerRefresh(s.refresh_interval);
      }
      const uid = me?.id ?? getIdentityId() ?? undefined;
      setUserId(uid);
      setOverrides(getUserOverrides(uid));
      setLoading(false);

      // Run health checks for all chains
      const userOvr = getUserOverrides(uid);
      for (const chain of c) {
        const url = userOvr.chains?.[chain.name]?.rpc_url || chain.rpcUrl;
        if (!url) continue;
        setRpcStatus((prev) => ({ ...prev, [chain.name]: "checking" }));
        rpcStatusOf(url, chain).then((st) => {
          setRpcStatus((prev) => ({ ...prev, [chain.name]: st }));
        });
      }
    });
  }, []);

  function save(next: UserOverrides) {
    setOverrides(next);
    setUserOverrides(next, userId);
    setExpertContext(next.preferences?.expert_mode ?? false); // JSON edit / import can flip it
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  }

  // ── Chain overrides ──

  function setChainField(name: string, field: "rpc_url" | "explorer_url", value: string) {
    if (value) trackExpertToolOnce("config_networks");
    const prev = overrides.chains ?? {};
    const chain = { ...prev[name] };
    if (value) {
      chain[field] = value;
    } else {
      delete chain[field];
    }
    const hasOverrides = Object.keys(chain).some((k) => chain[k as keyof typeof chain]);
    const updatedChains = { ...prev };
    if (hasOverrides) updatedChains[name] = chain;
    else delete updatedChains[name];
    save({ ...overrides, chains: Object.keys(updatedChains).length ? updatedChains : undefined });

    // Re-check RPC health when URL changes (debounced — wait 1s after typing stops)
    if (field === "rpc_url") {
      const chainData = chains.find((c) => c.name === name);
      const url = value || chainData?.rpcUrl || "";
      clearTimeout(healthCheckTimers.current[name]);
      if (url) {
        healthCheckTimers.current[name] = setTimeout(() => {
          setRpcStatus((prev) => ({ ...prev, [name]: "checking" }));
          rpcStatusOf(url, chainData ?? { type: "evm", evmChainId: null }).then((st) => {
            setRpcStatus((prev) => ({ ...prev, [name]: st }));
          });
        }, 1000);
      }
    }
  }

  function getChainField(name: string, field: "rpc_url" | "explorer_url"): string {
    return overrides.chains?.[name]?.[field] ?? "";
  }

  // ── Preferences ──

  function getRefreshInterval(): number {
    return overrides.preferences?.refresh_interval ?? serverRefresh;
  }

  function setRefreshInterval(value: number) {
    const prefs = { ...overrides.preferences };
    if (value === serverRefresh) {
      delete prefs.refresh_interval;
    } else {
      prefs.refresh_interval = value;
    }
    save({ ...overrides, preferences: Object.keys(prefs).length ? prefs : undefined });
  }

  function getShowTestnet(): boolean {
    return overrides.preferences?.show_testnet ?? false;
  }

  function setShowTestnet(value: boolean) {
    const prefs = { ...overrides.preferences };
    if (!value) {
      delete prefs.show_testnet;
    } else {
      prefs.show_testnet = true;
    }
    save({ ...overrides, preferences: Object.keys(prefs).length ? prefs : undefined });
  }

  function getExpertMode(): boolean {
    return overrides.preferences?.expert_mode ?? false;
  }

  function setExpertMode(value: boolean) {
    const prefs = { ...overrides.preferences };
    if (!value) {
      delete prefs.expert_mode;
    } else {
      prefs.expert_mode = true;
      trackExpertTool("expert_enabled");
    }
    save({ ...overrides, preferences: Object.keys(prefs).length ? prefs : undefined });
    setExpertContext(value);
  }

  function getConfirmBeforeBroadcast(): boolean {
    return overrides.preferences?.confirm_before_broadcast ?? false;
  }

  function setConfirmBeforeBroadcast(value: boolean) {
    const prefs = { ...overrides.preferences };
    if (!value) {
      delete prefs.confirm_before_broadcast;
    } else {
      prefs.confirm_before_broadcast = true;
      trackExpertTool("config_confirm_broadcast");
    }
    save({ ...overrides, preferences: Object.keys(prefs).length ? prefs : undefined });
  }

  const visibleChains = getShowTestnet() ? chains : chains.filter((c) => !isTestnetChain(c.name));

  function getDefaultChains(): string[] {
    return overrides.preferences?.default_chains ?? serverDefaults;
  }

  function toggleDefaultChain(name: string) {
    const current = getDefaultChains();
    const next = current.includes(name)
      ? current.filter((c) => c !== name)
      : [...current, name];
    const prefs = { ...overrides.preferences, default_chains: next };
    save({ ...overrides, preferences: prefs });
  }

  // ── Export / Import ──

  function exportConfig() {
    const json = JSON.stringify(overrides, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "kexify-config.json";
    a.click();
    URL.revokeObjectURL(url);
  }

  function copyConfig() {
    navigator.clipboard.writeText(JSON.stringify(overrides, null, 2));
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  }

  // Import and reset overwrite overrides (RPC choices may matter for privacy): confirm first
  const [pendingImport, setPendingImport] = useState<UserOverrides | null>(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const [importError, setImportError] = useState("");

  function importConfig(file: File) {
    setImportError("");
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result as string);
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) throw new Error("not an object");
        setPendingImport(parsed as UserOverrides);
      } catch {
        setImportError(t("config.invalidJson"));
      }
    };
    reader.onerror = () => setImportError(t("config.invalidJson"));
    reader.readAsText(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState("");

  async function emailConfig() {
    setEmailSending(true);
    setEmailError("");
    try {
      const res = await fetch(apiUrl("/api/settings/backup-email"), {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ config: overrides }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setEmailSent(true);
      setTimeout(() => setEmailSent(false), 3000);
    } catch (err) {
      setEmailSent(false);
      setEmailError(friendlyError(err, t).message);
    } finally {
      setEmailSending(false);
    }
  }

  function resetConfig() {
    clearUserOverrides(userId);
    setOverrides({});
    setExpertContext(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 1500);
  }

  // ── JSON editor ──

  const pendingMergedConfig = useMemo(() => {
    if (jsonTab !== "preview") return null;
    try {
      const pending = JSON.parse(jsonText) as UserOverrides;
      const mergedChains = chains.map((c) => {
        const fields = pending.chains?.[c.name] ?? {};
        return Object.keys(fields).length ? { ...c, ...fields } : c;
      });
      return {
        chains: mergedChains,
        assets,
        preferences: {
          ...serverSettings,
          default_chains: pending.preferences?.default_chains ?? serverDefaults,
          refresh_interval: pending.preferences?.refresh_interval ?? serverRefresh,
          ...pending.preferences,
        },
      };
    } catch {
      return null;
    }
  }, [jsonText, jsonTab, chains, assets, serverSettings, serverDefaults, serverRefresh]);

  function openJsonEditor() {
    trackExpertTool("config_json");
    setJsonText(JSON.stringify(overrides, null, 2));
    setJsonError("");
    setJsonTab("edit");
    setJsonMode(true);
  }

  function saveJsonEditor() {
    try {
      const parsed = JSON.parse(jsonText);
      save(parsed);
      setJsonMode(false);
    } catch {
      setJsonError(t("config.invalidJson"));
      setJsonTab("edit");
    }
  }

  function previewJsonEditor() {
    try {
      JSON.parse(jsonText);
      setJsonError("");
      setJsonTab("preview");
    } catch {
      setJsonError(t("config.invalidJsonFix"));
    }
  }

  const hasOverrides = Object.keys(overrides).length > 0 &&
    (Object.keys(overrides.chains ?? {}).length > 0 || Object.keys(overrides.preferences ?? {}).length > 0);

  if (loading) {
    return (
      <div className="space-y-5">
        <h2 className="text-lg font-semibold text-text-primary">{t("config.title")}</h2>
        <div className="text-xs text-text-muted text-center py-8">{t("common.loading")}</div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-semibold text-text-primary">{t("config.title")}</h2>
          <p className="text-xs text-text-muted mt-1">
            {t("config.description")}
            {saved && <span className="text-green-400 ml-2">{t("config.saved")}</span>}
          </p>
        </div>
        {expert && (
          <button
            onClick={openJsonEditor}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-text-muted hover:text-text-secondary hover:bg-surface-tertiary transition-colors"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.28z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            {t("config.json")}
          </button>
        )}
      </div>

      {!isRecoveryActive() && <PlanCard />}

      {/* ── Preferences ── */}
      <div>
        <p className="text-[10px] text-text-muted uppercase tracking-wider font-semibold mb-2 px-1">
          {t("config.preferences")}
        </p>
        <div className="bg-surface-secondary rounded-lg border border-border-primary overflow-hidden divide-y divide-border-secondary">
          {/* Language selector */}
          <LanguageSelector />

          {/* Theme: System / Light / Dark */}
          <ThemeSelector />

          {/* Notifications toggle */}
          <NotificationToggle />

          {/* Expert mode — first row, always visible */}
          <div className="px-3 md:px-5 py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-text-primary">{t("config.expertMode")}</p>
                <p className="text-xs text-text-muted mt-0.5">{t("config.expertModeDesc")}</p>
              </div>
              <button
                onClick={() => setExpertMode(!getExpertMode())}
                className={`relative w-8 h-[18px] rounded-full transition-colors shrink-0 ${
                  getExpertMode() ? "bg-blue-500" : "bg-surface-tertiary"
                }`}
              >
                <span className={`absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white transition-transform ${
                  getExpertMode() ? "left-[16px]" : "left-[2px]"
                }`} />
              </button>
            </div>
          </div>

          {/* Show test networks: a plain setting (developers, learning with free test coins), not an Expert tool */}
          <div className="px-3 md:px-5 py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-text-primary">{t("config.showTestNetworks")}</p>
                <p className="text-xs text-text-muted mt-0.5">{t("config.showTestNetworksDesc")}</p>
              </div>
              <button
                onClick={() => setShowTestnet(!getShowTestnet())}
                className={`relative w-8 h-[18px] rounded-full transition-colors shrink-0 ${
                  getShowTestnet() ? "bg-blue-500" : "bg-surface-tertiary"
                }`}
              >
                <span className={`absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white transition-transform ${
                  getShowTestnet() ? "left-[16px]" : "left-[2px]"
                }`} />
              </button>
            </div>
          </div>

          {/* Confirm before broadcast (expert only) */}
          {expert && <div className="px-3 md:px-5 py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-text-primary">{t("config.confirmBeforeBroadcast")}</p>
                <p className="text-xs text-text-muted mt-0.5">{t("config.confirmBeforeBroadcastDesc")}</p>
              </div>
              <button
                onClick={() => setConfirmBeforeBroadcast(!getConfirmBeforeBroadcast())}
                className={`relative w-8 h-[18px] rounded-full transition-colors shrink-0 ${
                  getConfirmBeforeBroadcast() ? "bg-blue-500" : "bg-surface-tertiary"
                }`}
              >
                <span className={`absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white transition-transform ${
                  getConfirmBeforeBroadcast() ? "left-[16px]" : "left-[2px]"
                }`} />
              </button>
            </div>
          </div>}

          {/* EVM gas buffer (expert only) */}
          {expert && <div className="px-3 md:px-5 py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-text-primary">{t("config.evmGasBuffer")}</p>
                <p className="text-xs text-text-muted mt-0.5">{t("config.evmGasBufferDesc")}</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min={0}
                  max={50}
                  value={overrides.preferences?.evm_gas_buffer_pct ?? 10}
                  onChange={(e) => {
                    const val = Math.max(0, Math.min(50, parseInt(e.target.value) || 0));
                    const prefs = { ...overrides.preferences };
                    if (val === 10) {
                      delete prefs.evm_gas_buffer_pct;
                    } else {
                      prefs.evm_gas_buffer_pct = val;
                      trackExpertToolOnce("config_gas_buffer");
                    }
                    save({ ...overrides, preferences: Object.keys(prefs).length ? prefs : undefined });
                  }}
                  className="w-16 bg-surface-primary border border-border-primary rounded-lg px-2.5 py-1.5 text-sm text-text-primary text-center font-mono focus:outline-none focus:border-blue-500 transition-colors"
                />
                <span className="text-xs text-text-muted">%</span>
              </div>
            </div>
          </div>}

          {/* Refresh interval */}
          <div className="px-3 md:px-5 py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-text-primary">{t("config.refreshInterval")}</p>
                <p className="text-xs text-text-muted mt-0.5">{t("config.refreshIntervalDesc")}</p>
              </div>
              <div className="flex bg-surface-primary border border-border-primary rounded-lg p-0.5 gap-0.5">
                {REFRESH_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => setRefreshInterval(opt.value)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      getRefreshInterval() === opt.value
                        ? "bg-surface-tertiary text-text-primary shadow-sm"
                        : "text-text-muted hover:text-text-secondary"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Default chains */}
          <div className="px-3 md:px-5 py-4">
            <p className="text-sm font-medium text-text-primary">{t("config.defaultChains")}</p>
            <p className="text-xs text-text-muted mt-0.5 mb-3">{t("config.defaultChainsDesc")}</p>
            <div className="flex flex-wrap gap-2">
              {chains.filter((c) => !isTestnetChain(c.name)).map((chain) => {
                const selected = getDefaultChains().includes(chain.name);
                return (
                  <button
                    key={chain.id}
                    onClick={() => toggleDefaultChain(chain.name)}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                      selected
                        ? "bg-blue-500/10 border-blue-500/30 text-blue-400"
                        : "bg-surface-primary border-border-primary text-text-muted hover:text-text-secondary hover:border-border-secondary"
                    }`}
                  >
                    {chain.iconUrl && (
                      <img src={chain.iconUrl} alt="" className="w-4 h-4 rounded-full" />
                    )}
                    {chain.displayName}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ── Networks (expert only) ── */}
      {expert && <div>
        <p className="text-[10px] text-text-muted uppercase tracking-wider font-semibold mb-2 px-1">
          {t("config.networks")}
        </p>
        <div className="bg-surface-secondary rounded-lg border border-border-primary overflow-hidden divide-y divide-border-secondary">
          {visibleChains.filter((c) => !c.custom).map((chain) => {
            const expanded = expandedChain === chain.name;
            const hasFieldOverrides = !!getChainField(chain.name, "rpc_url") || !!getChainField(chain.name, "explorer_url");

            return (
              <div key={chain.id}>
                <button
                  className="w-full flex items-center px-3 md:px-5 py-3 gap-3 text-left hover:bg-surface-tertiary/40 transition-colors"
                  onClick={() => setExpandedChain(expanded ? null : chain.name)}
                >
                  {chain.iconUrl ? (
                    <img src={chain.iconUrl} alt={chain.displayName} className="w-8 h-8 rounded-full bg-surface-tertiary shrink-0" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-surface-tertiary shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-text-primary leading-tight flex items-center gap-1.5">
                      {chain.displayName}
                      <TestnetBadge chainName={chain.name} />
                      {hasFieldOverrides && (
                        <span className="text-[10px] px-1 py-0.5 rounded bg-blue-500/10 text-blue-400 font-medium">{t("config.custom")}</span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                        rpcStatus[chain.name] === "ok" ? "bg-green-500 animate-heartbeat" :
                        rpcStatus[chain.name] === "error" || rpcStatus[chain.name] === "mismatch" ? "bg-red-400" :
                        rpcStatus[chain.name] === "checking" ? "bg-yellow-400 animate-pulse" :
                        "bg-surface-tertiary"
                      }`} />
                      <p className="text-[11px] text-text-muted font-mono truncate">
                        {getChainField(chain.name, "rpc_url") || chain.rpcUrl || "—"}
                      </p>
                    </div>
                  </div>
                  <svg className={`w-4 h-4 text-text-muted shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {expanded && (
                  <div className="px-3 md:px-5 pb-4 pt-1 space-y-3">
                    {rpcStatus[chain.name] === "mismatch" && (
                      <p className="text-xs text-red-400">{t("customNetwork.rpcMismatch", { chainId: String(chain.evmChainId) })}</p>
                    )}
                    <div>
                      <label className="block text-xs text-text-muted mb-1.5">{t("config.rpcUrl")}</label>
                      <div className="relative">
                        <input
                          value={getChainField(chain.name, "rpc_url")}
                          onChange={(e) => setChainField(chain.name, "rpc_url", e.target.value)}
                          placeholder={chain.rpcUrl || t("config.noDefaultRpc")}
                          className="w-full bg-surface-primary border border-border-primary rounded-lg px-3 py-2.5 pr-8 text-sm text-text-primary font-mono placeholder:text-text-muted focus:outline-none focus:border-blue-500 transition-colors"
                        />
                        {getChainField(chain.name, "rpc_url") && (
                          <button
                            onClick={() => setChainField(chain.name, "rpc_url", "")}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-text-muted hover:text-text-secondary transition-colors"
                            title={t("config.clear")}
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-text-muted mb-1.5">{t("config.explorerUrl")}</label>
                      <div className="relative">
                        <input
                          value={getChainField(chain.name, "explorer_url")}
                          onChange={(e) => setChainField(chain.name, "explorer_url", e.target.value)}
                          placeholder={chain.explorerUrl}
                          className="w-full bg-surface-primary border border-border-primary rounded-lg px-3 py-2.5 pr-8 text-sm text-text-primary font-mono placeholder:text-text-muted focus:outline-none focus:border-blue-500 transition-colors"
                        />
                        {getChainField(chain.name, "explorer_url") && (
                          <button
                            onClick={() => setChainField(chain.name, "explorer_url", "")}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-text-muted hover:text-text-secondary transition-colors"
                            title={t("config.clear")}
                          >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>}

      {expert && <CustomNetworksSection overrides={overrides} save={save} builtIn={chains.filter((c) => !c.custom)} />}

      {/* ── Address Book ── */}
      <AddressBookPanel />

      {/* ── Backup & Restore ── */}
      <div>
        <p className="text-[10px] text-text-muted uppercase tracking-wider font-semibold mb-2 px-1">
          {t("config.backupRestore")}
        </p>
        <div className="bg-surface-secondary rounded-lg border border-border-primary overflow-hidden divide-y divide-border-secondary">
          {/* Export */}
          <div className="px-3 md:px-5 py-4 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-text-primary">{t("config.exportConfig")}</p>
              <p className="text-xs text-text-muted mt-0.5">
                {t("config.exportConfigDesc")}
                {emailSent && <span className="text-green-400 ml-2">{t("config.sent")}</span>}
              </p>
              {emailError && <p role="alert" className="text-xs text-red-400 mt-1">{emailError}</p>}
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                onClick={copyConfig}
                className="p-2 rounded-lg bg-surface-primary border border-border-primary hover:border-border-secondary text-text-muted hover:text-text-secondary transition-colors"
                title={t("config.copyToClipboard")}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75" />
                </svg>
              </button>
              {!isStandaloneJwt() && (
              <button
                onClick={emailConfig}
                disabled={emailSending}
                className="p-2 rounded-lg bg-surface-primary border border-border-primary hover:border-border-secondary text-text-muted hover:text-text-secondary transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title={t("config.emailToMyself")}
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                </svg>
              </button>
              )}
              <button
                onClick={exportConfig}
                className="px-3 py-2 rounded-lg bg-surface-primary border border-border-primary hover:border-border-secondary text-xs font-medium text-text-secondary hover:text-text-primary transition-colors"
              >
                {t("config.download")}
              </button>
            </div>
          </div>

          {/* Import */}
          <div className="px-3 md:px-5 py-4 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm font-medium text-text-primary">{t("config.importConfig")}</p>
              <p className="text-xs text-text-muted mt-0.5">{t("config.importConfigDesc")}</p>
              {importError && <p role="alert" className="text-xs text-red-400 mt-1">{importError}</p>}
            </div>
            <div>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,.txt"
                className="hidden"
                onChange={(e) => { if (e.target.files?.[0]) importConfig(e.target.files[0]); }}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="px-3 py-2 rounded-lg bg-surface-primary border border-border-primary hover:border-border-secondary text-xs font-medium text-text-secondary hover:text-text-primary transition-colors"
              >
                {t("config.upload")}
              </button>
            </div>
          </div>

          {/* Reset */}
          {hasOverrides && (
            <div className="px-3 md:px-5 py-4 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-sm font-medium text-text-primary">{t("config.resetToDefaults")}</p>
                <p className="text-xs text-text-muted mt-0.5">{t("config.resetToDefaultsDesc")}</p>
              </div>
              <button
                onClick={() => setConfirmReset(true)}
                className="px-3 py-2 rounded-lg text-xs font-medium text-red-400 hover:bg-red-500/10 transition-colors"
              >
                {t("config.reset")}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Sign out (destructive, last) ── */}
      {!isRecoveryMode() && (
        <div className="bg-surface-secondary rounded-lg border border-border-primary overflow-hidden">
          <button
            onClick={() => setConfirmSignOut(true)}
            className="w-full px-3 md:px-5 py-4 text-left text-sm font-medium text-red-400 hover:bg-red-500/10 transition-colors"
          >
            {t("nav.signOut")}
          </button>
        </div>
      )}

      <SignOutConfirmDialog open={confirmSignOut} onCancel={() => setConfirmSignOut(false)} />
      <ConfirmDialog
        open={confirmReset}
        message={t("config.resetConfirm")}
        confirmLabel={t("config.reset")}
        destructive
        onConfirm={() => { setConfirmReset(false); resetConfig(); }}
        onCancel={() => setConfirmReset(false)}
      />
      <ConfirmDialog
        open={!!pendingImport}
        message={t("config.importConfirm")}
        confirmLabel={t("config.upload")}
        onConfirm={() => { if (pendingImport) save(pendingImport); setPendingImport(null); }}
        onCancel={() => setPendingImport(null)}
      />

      {/* ── JSON Editor Modal ── */}
      {jsonMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50" onClick={() => setJsonMode(false)} />
          <div className="relative bg-surface-secondary border border-border-primary rounded-2xl w-full max-w-lg shadow-xl flex flex-col max-h-[80vh]">
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-border-primary">
              <div className="flex items-center gap-3">
                <h3 className="text-sm font-semibold text-text-primary">{t("config.jsonConfig")}</h3>
                <div className="flex bg-surface-primary border border-border-primary rounded-lg p-0.5 gap-0.5">
                  <button
                    onClick={() => setJsonTab("edit")}
                    className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                      jsonTab === "edit" ? "bg-surface-tertiary text-text-primary shadow-sm" : "text-text-muted hover:text-text-secondary"
                    }`}
                  >
                    {t("config.edit")}
                  </button>
                  <button
                    onClick={() => setJsonTab("preview")}
                    className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                      jsonTab === "preview" ? "bg-surface-tertiary text-text-primary shadow-sm" : "text-text-muted hover:text-text-secondary"
                    }`}
                  >
                    {t("config.preview")}
                  </button>
                </div>
              </div>
              <button
                onClick={() => setJsonMode(false)}
                className="p-1.5 rounded-md text-text-tertiary hover:text-text-primary hover:bg-surface-tertiary transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Body */}
            <div className="px-5 py-5 flex-1 overflow-auto">
              {jsonTab === "edit" ? (
                <>
                  <p className="text-xs text-text-muted mb-2">{t("config.yourOverrides")}</p>
                  <div className="w-full h-72 bg-surface-primary border border-border-primary rounded-lg overflow-auto focus-within:border-blue-500 transition-colors">
                    <Editor
                      value={jsonText}
                      onValueChange={(v) => { setJsonText(v); setJsonError(""); }}
                      highlight={highlightCode}
                      padding={12}
                      style={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace", fontSize: 12, lineHeight: 1.6, minHeight: "100%" }}
                    />
                  </div>
                  {jsonError && (
                    <p className="text-xs text-red-400 mt-1.5">{jsonError}</p>
                  )}
                </>
              ) : (
                <>
                  <p className="text-xs text-text-muted mb-2">{t("config.previewMergedDesc")}</p>
                  {pendingMergedConfig ? (
                    <div className="w-full h-72 bg-surface-primary border border-border-primary rounded-lg overflow-auto">
                      <pre
                        className="px-3 py-2.5 text-xs font-mono leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: highlightCode(JSON.stringify(pendingMergedConfig, null, 2)) }}
                      />
                    </div>
                  ) : (
                    <div className="w-full h-72 bg-surface-primary border border-red-500/30 rounded-lg flex items-center justify-center">
                      <p className="text-xs text-red-400">{t("config.invalidJsonGoBack")}</p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            <div className="px-5 py-4 border-t border-border-primary flex justify-end gap-2">
              <button
                onClick={() => setJsonMode(false)}
                className="bg-surface-tertiary text-text-secondary hover:bg-border-primary px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
              >
                {t("common.cancel")}
              </button>
              {jsonTab === "edit" ? (
                <button
                  onClick={previewJsonEditor}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
                >
                  {t("config.preview")}
                </button>
              ) : (
                <>
                  <button
                    onClick={() => setJsonTab("edit")}
                    className="bg-surface-tertiary text-text-secondary hover:bg-border-primary px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
                  >
                    {t("common.back")}
                  </button>
                  <button
                    onClick={saveJsonEditor}
                    disabled={!pendingMergedConfig}
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-surface-tertiary disabled:text-text-muted text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors"
                  >
                    {t("common.save")}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LanguageSelector() {
  const { t } = useTranslation();
  const [lang, setLang] = useState(getStoredLanguage);

  function handleChange(code: string) {
    setLanguage(code);
    setLang(code);
  }

  return (
    <div className="px-3 md:px-5 py-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-text-primary">{t("config.language")}</p>
        </div>
        {/* Each language in its own name, so anyone can find theirs whatever the current language is. */}
        <select
          value={lang}
          onChange={(e) => handleChange(e.target.value)}
          aria-label={t("config.language")}
          className="max-w-[55%] bg-surface-primary border border-border-primary rounded-lg px-2.5 py-1.5 text-xs font-medium text-text-primary focus:outline-none focus:border-blue-500"
        >
          {LOCALES.map((l) => <option key={l.code} value={l.code}>{l.name}</option>)}
        </select>
      </div>
    </div>
  );
}
